import { useSubscription, useApolloClient } from '@apollo/client';
import { BlockCreatedPayload, BlockUpdatedPayload, CrewMember, PersonaType } from '@bechtel/types';
import { useEffect } from 'react';
import { SUBSCRIPTION_BLOCK_CREATED, SUBSCRIPTION_BLOCK_UPDATED } from '@bechtel/client-gql';
import { useBlockCache } from '~/hooks/use-block-cache';
import {
  useCommodityCache,
  useWorkPackageCache,
  useAppUser,
  useEnvProvider,
  ProjectAppTypes,
  CacheKeys,
} from '@bechtel/client-common';
import { includes } from 'lodash';

export function useBlockSubscription() {
  const { data: updatedBlockData } = useSubscription<{ blockUpdated: BlockUpdatedPayload }>(SUBSCRIPTION_BLOCK_UPDATED);
  const { data: createdBlockData } = useSubscription<{ blockCreated: BlockCreatedPayload }>(SUBSCRIPTION_BLOCK_CREATED);
  const { updateWorkPackagesCache } = useWorkPackageCache();
  const { updateCommoditiesCache } = useCommodityCache();
  const { updateBlockCache } = useBlockCache();
  const client = useApolloClient();
  const { user } = useAppUser();
  const { envConfig } = useEnvProvider();
  const dashboardSelectedUser: CrewMember = JSON.parse(sessionStorage.getItem(CacheKeys.DashboardSelectedUser));
  const corePersonaTypes = [
    PersonaType.SUPERINTENDENT,
    PersonaType.GENERAL_FOREMAN,
    PersonaType.FOREMAN,
    PersonaType.FIELD_ENGINEER,
  ];
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  const isASAppType = envConfig.appName === ProjectAppTypes.AS;

  const fetchDashboardStatForSuperGfForeman = async () => {
    await client.refetchQueries({
      include: ['commodities', 'commodityStats','commodityStatsPriority', 'commodityWeeklyStats', 'crewProgressStats','commodityDashboardTabStats'],
    });
  };

  const fetchDashboardStatForFWE = async () => {
    await client.refetchQueries({
      include: ['commodities', 'commodityStatsClaimedAndUnassignedFWE','commodityStatsRejectedFWE', 'commodityWeeklyStats', 'commodityFitupStats','commodityDashboardTabStats','commodityStatsAllPriorityStats','assignToMeStatsFWE','commodityBlocksStatsFWE'],
    });
  };
  useEffect(() => {
    if (
      !updatedBlockData ||
      !updatedBlockData.blockUpdated ||
      !updatedBlockData.blockUpdated.blockId ||
      !updatedBlockData.blockUpdated.workPackageId ||
      !updatedBlockData.blockUpdated.commodityIds ||
      updatedBlockData.blockUpdated.commodityIds.length == 0
    )
      return;
    updateWorkPackagesCache([updatedBlockData.blockUpdated.workPackageId]);
    //@ts-ignore as the exit conditions are alreday there. TypeScript ignore
    updateCommoditiesCache(updatedBlockData.blockUpdated.commodityIds);
    updateBlockCache(updatedBlockData.blockUpdated.blockId);
    if (
      !(
        includes(corePersonaTypes, user?.crewMember?.persona) ||
        includes(corePersonaTypes, dashboardSelectedUser?.persona)
      )
    )
      return;

    const affectedOwnerIds = updatedBlockData?.blockUpdated?.affectedOwnerIds;
    const pathName = location.pathname;

    if (
      isPFAppType &&
      pathName &&
      //Root path is Dashboard. Dangerous assumption. Find dynamic way to find path for dashboard
      pathName === '/' &&
      affectedOwnerIds &&
      (includes(affectedOwnerIds, user?.id) || includes(affectedOwnerIds, dashboardSelectedUser?.id))
    ) {
      if (
        includes(corePersonaTypes, user?.crewMember?.persona) ||
        includes(corePersonaTypes, dashboardSelectedUser?.persona)
      )
        client.refetchQueries({
          include: ['workPackageStats'],
        });
    }
    if (isASAppType && pathName && pathName === '/') {
      if (
        affectedOwnerIds &&
        (includes(affectedOwnerIds, user?.id) || includes(affectedOwnerIds, dashboardSelectedUser?.id)) &&
        (includes(corePersonaTypes, user?.crewMember?.persona) ||
          includes(corePersonaTypes, dashboardSelectedUser?.persona))
      ) {
        fetchDashboardStatForSuperGfForeman();
      }
    }
  }, [location, updatedBlockData, dashboardSelectedUser, user]);

  useEffect(() => {
    if (
      !createdBlockData ||
      !createdBlockData.blockCreated ||
      !createdBlockData.blockCreated.blockId ||
      !createdBlockData.blockCreated.workPackageId ||
      !createdBlockData.blockCreated.commodityIds ||
      createdBlockData.blockCreated.commodityIds.length == 0
    )
      return;
    updateWorkPackagesCache([createdBlockData.blockCreated.workPackageId]);
    //@ts-ignore as the exit conditions are already there. TypeScript ignore
    updateCommoditiesCache(createdBlockData.blockCreated.commodityIds);

    if (
      !(
        includes(corePersonaTypes, user?.crewMember?.persona) ||
        includes(corePersonaTypes, dashboardSelectedUser?.persona)
      )
    )
      return;

    const affectedOwnerIds = createdBlockData?.blockCreated?.affectedOwnerIds;
    const pathName = location.pathname;

    if (
      isPFAppType &&
      pathName &&
      //Root path is Dashboard. Dangerous assumption. Find dynamic way to find path for dashboard
      pathName === '/' &&
      affectedOwnerIds &&
      (includes(affectedOwnerIds, user?.id) || includes(affectedOwnerIds, dashboardSelectedUser?.id))
    ) {
      if (
        includes(corePersonaTypes, user?.crewMember?.persona) ||
        includes(corePersonaTypes, dashboardSelectedUser?.persona)
      )
        client.refetchQueries({
          include: ['workPackageStats'],
        });
    }
    if (isASAppType && pathName && pathName === '/') {
      if (
        affectedOwnerIds &&
        (includes(affectedOwnerIds, user?.id) || includes(affectedOwnerIds, dashboardSelectedUser?.id)) &&
        (includes(corePersonaTypes, user?.crewMember?.persona) ||
          includes(corePersonaTypes, dashboardSelectedUser?.persona))
      ) {
        fetchDashboardStatForSuperGfForeman();
      }
    }
  }, [location, createdBlockData, dashboardSelectedUser, user]);

  useEffect(() => {
    if (updatedBlockData || updatedBlockData?.blockUpdated || createdBlockData || createdBlockData?.blockCreated) {
      const pathName = location.pathname;
      if (isASAppType && pathName && pathName === '/') {
        if (
          includes([PersonaType.FIELD_WELD_ENGINEER], user?.crewMember?.persona) ||
          includes([PersonaType.FIELD_WELD_ENGINEER], dashboardSelectedUser?.persona)
        ) {
          fetchDashboardStatForFWE();
        }
      }
    }
  }, [location, createdBlockData, updatedBlockData, dashboardSelectedUser, user]);

  return null;
}
